import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PrepwiseLogo from '../components/PrepwiseLogo';
import './WaitingForInvite.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const WaitingForInvite: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, login } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const checkForInvite = useCallback(async () => {
    if (!user?.email) return;
    
    setIsChecking(true);
    try {
      // Check for pending invite
      const response = await fetch(
        `${API_URL}/auth/pending-invite?email=${encodeURIComponent(user.email)}`
      );
      
      if (!response.ok) {
        setLastChecked(new Date());
        return;
      }
      
      const data = await response.json();
      
      if (data.invite && data.invite.status === 'pending') {
        // Accept the invite
        const acceptResponse = await fetch(
          `${API_URL}/auth/accept-invite/${data.invite.id}?email=${encodeURIComponent(user.email)}`,
          { method: 'POST' }
        );
        
        if (acceptResponse.ok) {
          // Re-login to refresh user data
          await login({
            id: user.id,
            email: user.email,
            name: user.name,
            picture: user.picture,
          });
          
          // Navigate based on role
          if (data.invite.role === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/employee/dashboard');
          }
          return;
        }
      }
      
      setLastChecked(new Date());
    } catch (error) {
      console.error('Error checking for invite:', error);
    } finally {
      setIsChecking(false);
    }
  }, [user, login, navigate]);

  // Check on mount and every 30 seconds
  useEffect(() => {
    checkForInvite();
    
    const interval = setInterval(checkForInvite, 30000);
    return () => clearInterval(interval);
  }, [checkForInvite]);

  // If user already has an organization, redirect them
  useEffect(() => {
    if (user?.organizationId) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/employee/dashboard');
      }
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatLastChecked = () => {
    if (!lastChecked) return 'Never';
    return lastChecked.toLocaleTimeString();
  };

  return (
    <div className="waiting-container">
      <div className="waiting-card">
        <div className="waiting-header">
          <PrepwiseLogo size="large" />
          <h1>Waiting for Invite</h1>
        </div>
        
        <div className="waiting-content">
          <div className="waiting-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          
          <p className="waiting-message">
            Your account has been created, but you haven't been invited to any organization yet.
          </p>
          
          <p className="waiting-instructions">
            Please ask your organization administrator to invite you using your email:
          </p>
          
          <div className="user-email-display">
            <strong>{user?.email}</strong>
          </div>
          
          <p className="waiting-note">
            Once you're invited, this page will automatically redirect you to your dashboard.
          </p>
        </div>
        
        <div className="waiting-status">
          <span className="status-label">Last checked:</span>
          <span className="status-value">{formatLastChecked()}</span>
          {isChecking && <span className="checking-indicator">Checking...</span>}
        </div>
        
        <div className="waiting-actions">
          <button 
            className="check-button"
            onClick={checkForInvite}
            disabled={isChecking}
          >
            {isChecking ? 'Checking...' : 'Check Now'}
          </button>
          
          <button 
            className="logout-button"
            onClick={handleLogout}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaitingForInvite;
